import {App} from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/JwtService";
import {AxiosResponse, AxiosRequestConfig} from "axios";
import {useGlobalStore} from "@/pinia/global";

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App;

  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);
    ApiService.vueInstance.axios.defaults.baseURL = process.env.VUE_APP_API_URL;
  }

  /**
   * @description set the default HTTP request headers
   */
  public static setHeader(): void {
    ApiService.vueInstance.axios.defaults.headers.common[
      "Authorization"
      ] = `Bearer ${JwtService.getToken()}`;
    ApiService.vueInstance.axios.defaults.headers.common["Accept"] =
      "application/json";
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static query(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, params);
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param slug: string
   * @param params
   * @returns Promise<AxiosResponse>
   */
  public static get(
    resource: string,
    slug = "" as string,
    params = {}
  ): Promise<AxiosResponse> {
    let path = `${resource}/${slug}`;
    if (Object.keys(params).length) {
      const query = this.arrayToString(params).join('&');
      path = path + '?' + query;
    }
    return ApiService.vueInstance.axios.get(path);
  }

  public static arrayToString(data): Array<string> {
    const result = []
    for (const [key, value] of Object.entries(data)) {
      // @ts-ignore
      result.push(key + "=" + value)
    }
    return result;
  }

  /**
   * @description set the POST HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static post(
    resource: string,
    // params: AxiosRequestConfig
    params: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    const _globalStore = useGlobalStore();
    // Carry the concrete Echo connection through every app-owned mutation so
    // BookingModified can distinguish this tab from another tab signed in as
    // the same administrator. Preserve all per-request options and headers.
    // @ts-ignore - Echo is installed globally after realtime configuration
    const socketId = typeof window !== 'undefined' ? window.Echo?.socketId?.() : null;
    const requestConfig = socketId
      ? {
          ...config,
          headers: {
            'X-Socket-ID': socketId,
            ...(config?.headers ?? {}),
          },
        }
      : config;
    return ApiService.vueInstance.axios.post(`${resource}`, params, requestConfig)
      // .catch((error) => {
      // globalStore.addNotificationError(error.response.data.message);
    // });
  }

  public static postBlob(
    resource: string,
    // params: AxiosRequestConfig
    params: unknown
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.post(`${resource}`, params, {
      responseType: "blob",
    });
  }

  /**
   * @description send the UPDATE HTTP request
   * @param resource: string
   * @param slug: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static update(
    resource: string,
    slug: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params);
  }

  /**
   * @description Send the PUT HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static put(
    resource: string,
    params: unknown
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}`, params);
  }

  /**
   * @description Send the PATCH HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static patch(
    resource: string,
    params: unknown
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.patch(`${resource}`, params);
  }

  /**
   * @description Send the DELETE HTTP request
   * @param resource: string
   * @returns Promise<AxiosResponse>
   */
  public static delete(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.delete(resource);
  }
}

export default ApiService;
